import React, { useState, useEffect } from 'react';
import { 
  BadgeDollarSign, ArrowUpRight, CheckCircle2, Clock, 
  Search, RefreshCw, AlertCircle, Eye, ShieldAlert, Globe
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerSalaries = () => {
  const [activeTab, setActiveTab] = useState('salaries'); // 'salaries', 'withdrawals'
  const [salaries, setSalaries] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('India');
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [salRes, withRes] = await Promise.all([
        managerAPI.getSalaries(),
        managerAPI.getWithdrawals()
      ]);
      if (salRes.data?.success) {
        setSalaries(salRes.data.salaries || []);
        if (salRes.data.country) setCountry(salRes.data.country);
        if (salRes.data.month) setMonth(salRes.data.month);
      }
      if (withRes.data?.success) {
        setWithdrawals(withRes.data.withdrawals || []);
      }
    } catch (err) {
      console.error("Failed to load salaries & withdrawals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredSalaries = salaries.filter(s => {
    const term = search.toLowerCase();
    const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
    const xoxo = (s.xoxo_id || '').toLowerCase();
    const agency = (s.agency_name || '').toLowerCase();
    return name.includes(term) || xoxo.includes(term) || agency.includes(term);
  });

  const filteredWithdrawals = withdrawals.filter(w => {
    const term = search.toLowerCase();
    const name = `${w.first_name || ''} ${w.last_name || ''}`.toLowerCase();
    const xoxo = (w.xoxo_id || '').toLowerCase();
    return name.includes(term) || xoxo.includes(term);
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Salaries & Withdrawals</h1>
            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs uppercase flex items-center gap-1.5">
              <Globe size={13} /> {country} Locked
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Financial oversight for your assigned country. View-only access with strict security barriers against manual balance alterations.
          </p>
        </div>

        {/* Security Warning Pill */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-semibold">
          <Eye size={16} className="text-amber-400" />
          <span>View-Only Mode (Owner Protected)</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveTab('salaries')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'salaries'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <BadgeDollarSign size={16} />
            <span>Host Salaries ({month || 'Current'})</span>
          </button>

          <button
            onClick={() => setActiveTab('withdrawals')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'withdrawals'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <ArrowUpRight size={16} />
            <span>Payout Requests ({withdrawals.length})</span>
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search host or XOXO ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-[#0b0f19] border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors w-64"
          />
        </div>
      </div>

      {/* TAB 1: SALARIES */}
      {activeTab === 'salaries' && (
        <div className="bg-[#0b0f19] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-white/[0.02] text-xs uppercase text-slate-400 tracking-wider border-b border-white/5">
                <tr>
                  <th className="py-4 px-6">Host</th>
                  <th className="py-4 px-6">Agency</th>
                  <th className="py-4 px-6">Coins Achieved</th>
                  <th className="py-4 px-6">Target Level</th>
                  <th className="py-4 px-6">Valid Days / Mins</th>
                  <th className="py-4 px-6">70% Basic Safe</th>
                  <th className="py-4 px-6">30% Bonus (10d)</th>
                  <th className="py-4 px-6">Total Diamond Reward</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-400">
                      <RefreshCw size={20} className="animate-spin text-indigo-400 mx-auto mb-2" />
                      Loading country salaries...
                    </td>
                  </tr>
                ) : filteredSalaries.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-slate-400">
                      No host target records found in {country} for {month}.
                    </td>
                  </tr>
                ) : (
                  filteredSalaries.map((s, idx) => (
                    <tr key={idx} className="hover:bg-white/[0.01] transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-white">{s.first_name || 'Host'} {s.last_name || ''}</div>
                        <div className="text-xs font-mono text-indigo-400">{s.xoxo_id}</div>
                      </td>
                      <td className="py-4 px-6 font-semibold text-slate-300">
                        {s.agency_name || 'Independent'}
                      </td>
                      <td className="py-4 px-6 font-mono font-bold text-amber-400">
                        {Number(s.achieved_coins || 0).toLocaleString()} 🪙
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                          Level {Number(s.current_level || 0)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-bold text-white">{s.valid_days} / 10 Days</div>
                        <div className="text-xs text-slate-400">{Math.floor(s.duration_mins / 60)}h {s.duration_mins % 60}m</div>
                      </td>
                      <td className="py-4 px-6 font-mono font-bold text-emerald-400">
                        {Number(s.basic_salary || 0).toLocaleString()} 💎
                      </td>
                      <td className="py-4 px-6 font-mono font-bold text-cyan-400">
                        {Number(s.bonus_salary || 0).toLocaleString()} 💎
                        {s.valid_days < 10 && (
                          <div className="text-[10px] text-amber-400 font-sans font-semibold">Requires 10 days</div>
                        )}
                      </td>
                      <td className="py-4 px-6 font-mono font-extrabold text-white">
                        {Number(s.total_salary || 0).toLocaleString()} 💎
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: WITHDRAWALS */}
      {activeTab === 'withdrawals' && (
        <div className="bg-[#0b0f19] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-white/[0.02] text-xs uppercase text-slate-400 tracking-wider border-b border-white/5">
                <tr>
                  <th className="py-4 px-6">Applicant</th>
                  <th className="py-4 px-6">Role</th>
                  <th className="py-4 px-6">Requested Diamonds</th>
                  <th className="py-4 px-6">Payout Amount</th>
                  <th className="py-4 px-6">Payment Method</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-slate-400">
                      <RefreshCw size={20} className="animate-spin text-indigo-400 mx-auto mb-2" />
                      Loading withdrawal requests...
                    </td>
                  </tr>
                ) : filteredWithdrawals.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-slate-400">
                      No withdrawal requests found for {country}.
                    </td>
                  </tr>
                ) : (
                  filteredWithdrawals.map((w) => (
                    <tr key={w.id} className="hover:bg-white/[0.01] transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-white">{w.first_name || 'User'} {w.last_name || ''}</div>
                        <div className="text-xs font-mono text-indigo-400">{w.xoxo_id}</div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-white/5 text-slate-300">
                          {w.user_type || 'HOST'}
                        </span>
                      </td>
                      <td className="py-4 px-6 font-mono font-bold text-cyan-400">
                        {Number(w.amount || w.diamonds || 0).toLocaleString()} 💎
                      </td>
                      <td className="py-4 px-6 font-mono font-bold text-emerald-400">
                        ₹{Number(w.inr_amount || w.fiat_amount || 0).toLocaleString()}
                      </td>
                      <td className="py-4 px-6 text-xs text-slate-300">
                        {w.payment_method || w.bank_name || 'Bank Transfer'}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          w.status === 'completed' || w.status === 'approved' 
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : w.status === 'rejected'
                            ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          {w.status || 'Pending'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-xs text-slate-400">
                        {new Date(w.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerSalaries;
